// Pure helpers for the admin « Coupons » page (2026-10-28) — no React, no DOM.
// Plan: docs/superpowers/plans/2026-10-28-coupons.md (CONTRACTS are binding).
//
// A coupon is 148.5 × 100 mm, landscape (3:2, the owner's 1536×1024 design).
// A design's boxes are 0..1 fractions of the design image, so the same numbers
// place the code in the editor preview, in the album PDF and in the separate
// coupon PDF.
import type {
  CouponBox, CouponBoxRole, CouponCampaign, CouponCell, CouponCodeRow, CouponCodeStatus,
  CouponRefusal, LayoutTemplateSlot,
} from '@/lib/api';

/** Physical coupon size (mm). */
export const COUPON_MM = { w: 148.5, h: 100 } as const;

/** Pixel size of a design rasterised at `dpi` (a PDF upload is turned into an image at 300 dpi). */
export function designTargetPx(dpi = 300): { w: number; h: number } {
  return { w: Math.round((COUPON_MM.w / 25.4) * dpi), h: Math.round((COUPON_MM.h / 25.4) * dpi) };
}

/** True when an image is (close to) the coupon's 3:2-ish landscape shape
 *  (148.5:100; the owner's 1536×1024 design passes). */
export function isCouponShape(width: number, height: number): boolean {
  if (!(width > 0 && height > 0)) return false;
  return Math.abs(width / height - COUPON_MM.w / COUPON_MM.h) < 0.06;
}

/** The sample codes drawn in the preview (owner's examples). */
export const SAMPLE_CODES: Record<Exclude<CouponBoxRole, 'qr' | 'patch'>, string> = {
  self_code: 'MERCI-1216-K7Q',
  gift_code: 'AMIE-1216-P3X',
};

/** Fonts the coupon PDF can draw (pdf-lib standard fonts) + their CSS twin for the preview. */
export const COUPON_FONTS: ReadonlyArray<{ id: string; label: string; css: string; weight: number }> = [
  { id: 'Helvetica-Bold', label: 'Helvetica gras', css: 'Helvetica, Arial, sans-serif', weight: 700 },
  { id: 'Helvetica', label: 'Helvetica', css: 'Helvetica, Arial, sans-serif', weight: 400 },
  { id: 'Courier-Bold', label: 'Courier gras (machine à écrire)', css: '"Courier New", Courier, monospace', weight: 700 },
  { id: 'Times-Bold', label: 'Times gras', css: '"Times New Roman", Times, serif', weight: 700 },
];
export const DEFAULT_FONT = 'Helvetica-Bold';
/** PRINT colours (data, not UI): the default ink of a code and the paper. */
export const DEFAULT_INK = '#2E2418';
export const PAPER = '#FFFFFF';

export function fontCss(id: string | undefined): { family: string; weight: number } {
  const f = COUPON_FONTS.find((x) => x.id === id) ?? COUPON_FONTS[0];
  return { family: f.css, weight: f.weight };
}

export const ROLE_LABELS: Record<CouponBoxRole, string> = {
  self_code: 'Code client',
  gift_code: 'Code amie',
  qr: 'QR code',
  patch: 'Cache (fond uni)',
};

const ROLE_ORDER: CouponBoxRole[] = ['self_code', 'gift_code', 'qr'];
/** Every role the box editor offers, in its menu order. */
export const ALL_ROLES: readonly CouponBoxRole[] = ['self_code', 'gift_code', 'qr', 'patch'];

/** The role a newly drawn box gets: the first one the design does not have yet. */
export function nextRole(boxes: readonly CouponBox[]): CouponBoxRole {
  return ROLE_ORDER.find((r) => !boxes.some((b) => b.role === r)) ?? 'self_code';
}

export function defaultBox(role: CouponBoxRole, geom: { x: number; y: number; w: number; h: number }): CouponBox {
  if (role === 'qr') return { role, ...geom, qr_target: 'prefill' };
  if (role === 'patch') return { role, ...geom, bg: PAPER };
  return { role, ...geom, font: DEFAULT_FONT, color: DEFAULT_INK, size_pct: 70, align: 'center' };
}

/** CouponBox (x,y,w,h) → the SlotEditor's slot (x,y,width,height). */
export function boxesToSlots(boxes: readonly CouponBox[]): LayoutTemplateSlot[] {
  return boxes.map((b) => ({ x: b.x, y: b.y, width: b.w, height: b.h }));
}

const near = (a: number, b: number) => Math.abs(a - b) < 1e-9;
const sameGeom = (b: CouponBox, s: LayoutTemplateSlot) =>
  near(b.x, s.x) && near(b.y, s.y) && near(b.w, s.width) && near(b.h, s.height);

/**
 * Fold the SlotEditor's new slot list back into boxes, keeping each box's
 * role and style. The editor does exactly one of three things per change:
 * moves/resizes one slot (same length), appends a drawn slot (+1) or
 * deletes one slot by index (−1, the others keep their order).
 */
export function mergeSlotsIntoBoxes(boxes: readonly CouponBox[], slots: readonly LayoutTemplateSlot[]): CouponBox[] {
  const clamp = (s: LayoutTemplateSlot) => ({
    x: Math.max(0, Math.min(1, s.x)),
    y: Math.max(0, Math.min(1, s.y)),
    w: Math.max(0, Math.min(1, s.width)),
    h: Math.max(0, Math.min(1, s.height)),
  });
  if (slots.length === boxes.length) {
    return boxes.map((b, i) => ({ ...b, ...clamp(slots[i]) }));
  }
  if (slots.length === boxes.length + 1) {
    const added = slots[slots.length - 1];
    const kept = boxes.map((b, i) => ({ ...b, ...clamp(slots[i]) }));
    return [...kept, defaultBox(nextRole(kept), clamp(added))];
  }
  if (slots.length === boxes.length - 1) {
    let gone = boxes.length - 1;
    for (let i = 0; i < slots.length; i++) {
      if (!sameGeom(boxes[i], slots[i])) { gone = i; break; }
    }
    return boxes.filter((_, i) => i !== gone);
  }
  // Anything else (should not happen): rebuild positionally, new boxes get defaults.
  return slots.map((s, i) => (boxes[i] ? { ...boxes[i], ...clamp(s) } : defaultBox(nextRole(boxes), clamp(s))));
}

/** Width of one character as a fraction of the font size — a deliberately wide
 *  estimate (codes are capitals and digits) so the preview never overflows. */
export const CHAR_W_EM = 0.68;

/**
 * Where and how big the code text sits when the design is drawn `renderW` ×
 * `renderH` pixels. size_pct = text height as a % of the box height (default
 * 70); the text then shrinks to fit the box width.
 */
export function codeTextLayout(
  box: CouponBox,
  renderW: number,
  renderH: number,
  text: string,
): { left: number; top: number; width: number; height: number; fontPx: number; textAlign: 'left' | 'center' | 'right' } {
  const left = box.x * renderW;
  const top = box.y * renderH;
  const width = box.w * renderW;
  const height = box.h * renderH;
  const pct = Math.max(10, Math.min(100, box.size_pct ?? 70));
  const byHeight = (height * pct) / 100;
  const byWidth = text.length > 0 ? width / (text.length * CHAR_W_EM) : byHeight;
  const fontPx = Math.max(1, Math.min(byHeight, byWidth));
  return { left, top, width, height, fontPx, textAlign: box.align ?? 'center' };
}

/** The sample text a box shows in the preview (null for a QR or patch box). */
export function sampleTextFor(role: CouponBoxRole): string | null {
  return role === 'qr' || role === 'patch' ? null : SAMPLE_CODES[role];
}

/** The regular face (preview) of box.after next to a code — couponPdf.afterFontId's twin. */
export function afterFontCss(id: string | undefined): { family: string; weight: number } {
  return { family: fontCss(id).family, weight: 400 };
}

/** The QR's sample link, as the PDF will encode it. */
export function qrSampleUrl(box: CouponBox, reviewUrl?: string | null): string {
  if (box.qr_target === 'review') return reviewUrl || '(lien d’avis — à régler)';
  return `https://storiz.ma/?code=${SAMPLE_CODES.gift_code}`;
}

/** Always exactly 4 cells (2×2 sheet, reading order: top-left, top-right, bottom-left, bottom-right). */
export function normalizeCells(cells: readonly Partial<CouponCell>[] | null | undefined): CouponCell[] {
  const out: CouponCell[] = [];
  for (let i = 0; i < 4; i++) {
    const c = cells?.[i];
    out.push({ front_design_id: c?.front_design_id ?? null, back_design_id: c?.back_design_id ?? null });
  }
  return out;
}

/** Fill the empty cells of a campaign with its first active front/back design. */
export function fillEmptyCells(cells: readonly CouponCell[], frontId: string | null, backId: string | null): CouponCell[] {
  return normalizeCells(cells).map((c) => ({
    front_design_id: c.front_design_id ?? frontId,
    back_design_id: c.back_design_id ?? backId,
  }));
}

export function discountLabel(c: Pick<CouponCampaign, 'discount_type' | 'discount_value'>): string {
  const v = Number(c.discount_value) || 0;
  return c.discount_type === 'percent' ? `${v} %` : `${v} DH`;
}

export function expiryLabel(c: Pick<CouponCampaign, 'expiry_mode' | 'expiry_days' | 'expiry_date'>): string {
  if (c.expiry_mode === 'fixed_date') {
    if (!c.expiry_date) return 'Date fixe (à choisir)';
    const [y, m, d] = c.expiry_date.slice(0, 10).split('-');
    return `Jusqu’au ${d}/${m}/${y}`;
  }
  const n = c.expiry_days || 30;
  return `${n} jour${n > 1 ? 's' : ''} après la livraison`;
}

/** A code's state: the list's detailed `state`, else its `status`, else derived
 *  (void → used → expired → issued). */
export function codeStatus(row: Pick<CouponCodeRow, 'status' | 'void_at' | 'used_at' | 'used_order_id' | 'expires_at'> & { state?: CouponCodeStatus }, now: Date = new Date()): CouponCodeStatus {
  if (row.state) return row.state;
  if (row.status) return row.status;
  if (row.void_at) return 'void';
  if (row.used_at || row.used_order_id) return 'used';
  if (row.expires_at && new Date(row.expires_at).getTime() <= now.getTime()) return 'expired';
  return 'issued';
}

export const STATUS_LABELS: Record<CouponCodeStatus, string> = {
  active: 'Valable',
  not_yet: 'Attend la livraison',
  reserved: 'Réservé (commande en cours)',
  issued: 'Émis',
  used: 'Utilisé',
  expired: 'Expiré',
  void: 'Annulé',
};

/** The list's 3 filters (émis / utilisés / expirés) over the worker's statuses. */
export type CodeFilter = '' | 'issued' | 'used' | 'expired';
export function codeFilterGroup(st: CouponCodeStatus): Exclude<CodeFilter, ''> | null {
  if (st === 'used') return 'used';
  if (st === 'expired') return 'expired';
  if (st === 'void') return null;
  return 'issued'; // active, not_yet, reserved, issued
}

/** French reason shown when a code is refused. */
export function refusalMessage(reason: CouponRefusal | string | undefined, minOrderDh?: number): string {
  switch (reason) {
    case 'unknown': return 'Ce code n’existe pas.';
    case 'used': return 'Ce code a déjà été utilisé.';
    case 'expired': return 'Ce code a expiré.';
    case 'not_yet': return 'Ce code n’est pas encore valable (la commande qui l’a reçu n’est pas encore livrée).';
    case 'min_order': return minOrderDh ? `Commande trop petite : minimum ${minOrderDh} DH.` : 'Commande trop petite pour ce code.';
    case 'inactive': return 'Cette campagne de coupons est arrêtée.';
    case 'already_has_coupon': return 'Cette commande a déjà un coupon (non cumulable).';
    case 'own_order': return 'Ce code a été offert par cette commande : il ne peut servir que sur une prochaine commande.';
    case 'max_uses': return 'Ce code a atteint son nombre maximum d’utilisations.';
    case 'once_per_client': return 'Ce client a déjà utilisé ce code sur une autre commande (une fois par client).';
    case 'locked': return 'Cette commande ne peut plus recevoir de coupon (annulée).';
    case 'disabled': return 'Les codes ne sont pas acceptés : activez « Accepter les codes sur le site » dans Paramètres.';
    default: return 'Code refusé.';
  }
}

/** A prefix as the codes print it: capitals and digits only, 1–12 characters. */
export function normalizePrefix(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
}

/** A code as typed by staff: trimmed, upper-case, inner spaces removed. */
export function normalizeCodeInput(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, '');
}

export type CampaignDraft = Omit<CouponCampaign, 'id' | 'created_at' | 'updated_at'>;

export function campaignDefaults(): CampaignDraft {
  return {
    name: '',
    discount_type: 'fixed',
    discount_value: 50,
    min_order_dh: 399,
    combinable: false,
    expiry_mode: 'after_delivery',
    expiry_days: 30,
    expiry_date: null,
    active: false,
    print_in_albums: true,
    print_priority: 100,
    max_codes: null,
    self_prefix: 'MERCI',
    gift_prefix: 'AMIE',
    shared_code: null,
    max_uses: null,
    once_per_client: true,
    influencer_name: null,
    commission_type: null,
    commission_value: null,
    cells: normalizeCells([]),
  };
}

/** French problems that block saving; [] = ok. */
export function campaignProblems(d: CampaignDraft): string[] {
  const out: string[] = [];
  if (!d.name.trim()) out.push('Donnez un nom à la campagne.');
  const v = Number(d.discount_value);
  if (!(v > 0)) out.push('La valeur de la réduction doit être plus grande que 0.');
  if (d.discount_type === 'percent' && v > 100) out.push('Un pourcentage ne peut pas dépasser 100 %.');
  if (!(Number(d.min_order_dh) >= 0)) out.push('La commande minimum doit être un nombre (0 ou plus).');
  if (d.expiry_mode === 'after_delivery' && !(Number(d.expiry_days) >= 1)) out.push('Le nombre de jours doit être au moins 1.');
  if (d.expiry_mode === 'fixed_date' && !d.expiry_date) out.push('Choisissez la date d’expiration.');
  if (!normalizePrefix(d.self_prefix)) out.push('Le préfixe du code client est vide.');
  if (!normalizePrefix(d.gift_prefix)) out.push('Le préfixe du code amie est vide.');
  if (normalizePrefix(d.self_prefix) === normalizePrefix(d.gift_prefix) && normalizePrefix(d.self_prefix))
    out.push('Les deux préfixes doivent être différents.');
  if (d.max_codes != null && !(Number(d.max_codes) >= 1)) out.push('Le nombre maximum de codes doit être au moins 1 (ou vide = illimité).');
  if (d.max_uses != null && !(Number(d.max_uses) >= 1)) out.push('Le nombre maximum d’utilisations doit être au moins 1 (ou vide = illimité).');
  if (d.commission_type) {
    const c = Number(d.commission_value);
    if (!(c > 0)) out.push('Indiquez la commission de l’influenceur (plus grande que 0).');
    if (d.commission_type === 'percent' && c > 100) out.push('Une commission en % ne peut pas dépasser 100 %.');
  }
  return out;
}

/** « 10 % par commande livrée » / « 30 DH par commande livrée »; null = no commission. */
export function commissionLabel(c: { commission_type?: 'fixed' | 'percent' | null; commission_value?: number | null }): string | null {
  const v = Number(c.commission_value);
  if (!c.commission_type || !(v > 0)) return null;
  return c.commission_type === 'percent'
    ? `${v.toLocaleString('fr-MA')} % par commande livrée`
    : `${v.toLocaleString('fr-MA')} DH par commande livrée`;
}

/** Money as the page shows it: « 1 100 DH » (2 decimals only when there are some). */
export function dh(n: number | null | undefined): string {
  const v = Number(n) || 0;
  const cents = Number.isInteger(v) ? 0 : 2;
  return `${v.toLocaleString('fr-MA', { minimumFractionDigits: cents, maximumFractionDigits: cents })} DH`;
}

/** The order page's « Coupon » block: only when codes are accepted, or when the
 *  order already carries one (so a coupon never silently disappears from view). */
export function showOrderCouponBlock(
  settings: { coupon_redeem_enabled?: boolean } | null | undefined,
  order: { coupon_code?: string | null } | null | undefined,
): boolean {
  return !!settings?.coupon_redeem_enabled || !!order?.coupon_code;
}

/** French words for a failed apply: a refusal reason from the worker (in the
 *  2xx body or the error body), a missing permission, or the raw message. */
export function couponErrorMessage(err: unknown): string {
  // A 2xx refusal ({ok:false, reason}) handed in directly.
  const direct = err as { ok?: boolean; reason?: string; min_order_dh?: number } | null;
  if (direct && direct.ok === false && direct.reason) return refusalMessage(direct.reason, direct.min_order_dh);
  const e = err as { status?: number; message?: string; body?: { reason?: string; min_order_dh?: number; error?: string } } | null;
  const reason = e?.body?.reason;
  if (reason) return refusalMessage(reason, e?.body?.min_order_dh);
  if (e?.status === 403) return 'Vous n’avez pas le droit d’appliquer un coupon sur cette commande.';
  if (e?.status === 404) return 'Commande ou code introuvable.';
  return e?.message || 'Code refusé.';
}

/** Rapports line figures; null = no coupon yet (the line is hidden). */
export function couponsReportText(s: { printed: number; used: number; revenue_dh: number } | null | undefined): { p: string; u: string; r: string } | null {
  if (!s) return null;
  const printed = Number(s.printed) || 0;
  const used = Number(s.used) || 0;
  if (printed === 0 && used === 0) return null;
  const fmt = (n: number) => Math.round(n).toLocaleString('fr-MA');
  return { p: fmt(printed), u: fmt(used), r: fmt(Number(s.revenue_dh) || 0) };
}
